// NomOS client: the one fetch wrapper behind the TZ-2 seam (SEN-395).
// NomOS is the only engine: helpers here transport requests and reshape
// envelopes; nothing in this directory may recompute aggregates client-side.
import { AxiosError } from 'axios';
import { activeSessionSlug } from './sessions';
import { clearReadCache, isRead, noteWrite, onAttributeInvalidation, readPolicy, sharedRead, type RawResponse } from './cache';
import { batchedRead, configureBatching, prioritise, resetBatchState, type ReadPriority } from './batch';

export interface NomosConfig {
  baseUrl: string;
  tenant: string;
  /** Bearer token; when undefined it is read from localStorage `nomos_token` per call. */
  token?: string;
}

/**
 * Test/harness injection: `defaultTenant` stands in for VITE_NOMOS_TENANT (the build default). An injected config
 * sends every read on its own unless `batch: true` (tests assert individual requests; see ./batch).
 */
type NomosOverride = Partial<NomosConfig> & { defaultTenant?: string; batch?: boolean };

let override: NomosOverride | null = null;

/** Inject config (Node parity harness, tests). Pass null to clear. */
export function configureNomos(cfg: NomosOverride | null): void {
  override = cfg;
  clearReadCache();
  resetBatchState();
  configureBatching(cfg === null ? null : { enabled: cfg.batch === true });
}

function viteEnv(name: string): string | undefined {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    return env?.[name];
  } catch {
    return undefined;
  }
}

function localToken(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('nomos_token') : null;
  } catch {
    return null;
  }
}

/**
 * NomOS root as configured, or null when NomOS is not configured. `/` (or any value that is only slashes)
 * means SAME ORIGIN: the SPA is served by the NomOS host's Caddy next to /v1/* (Tenant Zero B5/B6), and every
 * call is a relative path. Returned without a trailing slash, so same-origin is ''.
 */
export function nomosRoot(): string | null {
  const raw = override?.baseUrl ?? viteEnv('VITE_NOMOS_API_URL');
  if (raw === undefined || raw === null || raw.trim() === '') return null;
  return raw.trim().replace(/\/+$/, '');
}

/**
 * Active tenant slug. Runtime first (B7): the tenant of the signed-in session (./tenant.ts, verified against
 * GET /me). VITE_NOMOS_TENANT is only an optional default (dev, single-tenant builds, login pre-fill).
 */
export function activeTenantSlug(): string {
  return override?.tenant ?? activeSessionSlug() ?? defaultTenantSlug();
}

/** Default tenant from the build (VITE_NOMOS_TENANT), or ''. */
export function defaultTenantSlug(): string { return override?.defaultTenant ?? viteEnv('VITE_NOMOS_TENANT') ?? ''; }

// ---- Per-tenant cache reset ----
// Module caches in ./impl are per tenant. Each registers a clear function; when the resolved base|tenant
// changes (login to another customer, the tenant switcher, configureNomos) every registered cache is dropped
// before the first request of the new tenant is built.
const tenantCacheClears = new Set<() => void>();
let lastScope: string | null = null;
export function registerTenantCache(clear: () => void): void { tenantCacheClears.add(clear); }
export function resetTenantCaches(): void { tenantCacheClears.forEach((c) => c()); }
registerTenantCache(clearReadCache);
function noteScope(scope: string): void {
  if (lastScope !== null && lastScope !== scope) resetTenantCaches();
  lastScope = scope;
}

export function getConfig(): NomosConfig {
  const root = nomosRoot();
  const tenant = activeTenantSlug();
  if (root === null) throw new Error('NomOS: VITE_NOMOS_API_URL is not set');
  if (!tenant) throw new Error('NomOS: no tenant (sign in, or set VITE_NOMOS_TENANT for dev)');
  noteScope(`${root}|${tenant}`);
  return { baseUrl: root, tenant, token: override?.token ?? localToken() ?? undefined };
}

/** NomOS server root (no tenant), or '' when unset or same-origin. Does not require a tenant: the public field page has none. */
export function nomosBaseUrl(): string {
  return nomosRoot() ?? '';
}

// ---- Wire shapes (monad/plenum envelope) ----
export interface NomosPagination { limit: number; offset: number; total?: number; has_more?: boolean }
export interface NomosEnvelope<T> { data: T; meta?: { pagination?: NomosPagination; warnings?: unknown[]; [k: string]: unknown } }
export interface NomosFilterLeaf { attribute: string; op: string; value?: unknown }
export type NomosFilter = NomosFilterLeaf | { and: NomosFilter[] } | { or: NomosFilter[] };
export interface NomosRow { record_id: string; kenning: string; values: Record<string, unknown> }
export interface NomosPage { rows: NomosRow[]; total: number; limit: number; offset: number }

/**
 * Map a NomOS failure to the shape the UI already gets from axios:
 * an AxiosError with `response.status` and `response.data.detail` (string).
 * NomOS errors arrive as {error:{code,message,...}} (plenum) or FastAPI {detail}.
 */
export function toAxiosError(status: number, body: unknown, url: string): AxiosError {
  const b = (body ?? {}) as { detail?: unknown; error?: { message?: string; code?: string } };
  const detail = typeof b.detail === 'string' ? b.detail : b.error?.message ?? (b.detail ? JSON.stringify(b.detail) : `NomOS request failed (${status})`);
  const response = {
    status,
    statusText: '',
    data: { detail, ...(b.error ? { error: b.error } : {}) },
    headers: {},
    config: { url } as never,
  };
  return new AxiosError(detail, status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST', undefined, undefined, response as never);
}

export type { ReadPriority };

export async function nomosFetch<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  opts: { tenantScoped?: boolean; query?: Record<string, string | number | undefined>; priority?: ReadPriority } = {},
): Promise<T> {
  const tenantScoped = opts.tenantScoped ?? true;
  const cfg: Omit<NomosConfig, 'tenant'> & { tenant?: string } = tenantScoped ? getConfig() : unscopedConfig();
  const qs = opts.query
    ? '?' + Object.entries(opts.query).filter(([, v]) => v !== undefined).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
    : '';
  const url = `${cfg.baseUrl}${tenantScoped ? `/v1/tenants/${encodeURIComponent(cfg.tenant ?? '')}` : ''}${path}${qs}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (cfg.token) headers.Authorization = `Bearer ${cfg.token}`;
  const payload = body === undefined ? undefined : JSON.stringify(body);
  const send = async (): Promise<RawResponse> => {
    let res: Response;
    try {
      res = await fetch(url, { method, headers, body: payload });
    } catch (e) {
      throw new AxiosError((e as Error).message, 'ERR_NETWORK');
    }
    return { ok: res.ok, status: res.status, text: await res.text(), url };
  };
  let raw: RawResponse;
  activity.active++; activity.last = Date.now();
  try {
    raw = await transport(method, path, url, payload, cfg, tenantScoped, send, opts.priority);
  } finally {
    activity.active--; activity.last = Date.now();
  }
  let parsed: unknown = undefined;
  if (raw.text) { try { parsed = JSON.parse(raw.text); } catch { parsed = { detail: raw.text } } }
  if (!raw.ok) throw toAxiosError(raw.status, parsed, url);
  return parsed as T;
}

/** The table formats every NomOS read answers as a file (monad/plenum/tabular.py): the same read, `Accept` picks the bytes. */
export const TABLE_MEDIA = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const;
export type TableFormat = keyof typeof TABLE_MEDIA;

/** The file name a response's Content-Disposition gives (RFC 5987 `filename*` first), else `fallback`. */
export function dispositionName(header: string | null, fallback: string): string {
  const star = header?.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) { try { return decodeURIComponent(star[1].trim()); } catch { /* fall through */ } }
  const plain = header?.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : fallback;
}

/**
 * Bytes from NomOS (a stored file, or a read answered as a file): its own fetch - never the JSON read cache or
 * the /batch transport - with the same auth and the same error mapping as nomosFetch (a refusal stays the JSON
 * envelope, so the caller gets the engine's own message).
 */
export async function nomosBytes(
  method: string, path: string, opts: { body?: unknown; accept?: string; query?: Record<string, string> } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const cfg = getConfig();
  const qs = opts.query ? `?${new URLSearchParams(opts.query).toString()}` : '';
  const url = `${cfg.baseUrl}/v1/tenants/${encodeURIComponent(cfg.tenant)}${path}${qs}`;
  const headers: Record<string, string> = {};
  if (opts.accept) headers.Accept = opts.accept;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (cfg.token) headers.Authorization = `Bearer ${cfg.token}`;
  let res: Response;
  activity.active++; activity.last = Date.now();
  try {
    res = await fetch(url, { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body) });
  } catch (e) {
    throw new AxiosError((e as Error).message, 'ERR_NETWORK');
  } finally {
    activity.active--; activity.last = Date.now();
  }
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = undefined;
    if (text) { try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; } }
    throw toAxiosError(res.status, parsed, url);
  }
  const disposition = res.headers?.get?.('content-disposition') ?? null;
  return { blob: await res.blob(), filename: disposition ? dispositionName(disposition, '') || null : null };
}

/** A read (`POST /queries`, `POST /objects/{o}/records/query`) answered as a CSV/XLSX file: its whole answer. */
export function exportRead(path: string, body: Record<string, unknown>, format: TableFormat) {
  return nomosBytes('POST', path, { body, accept: TABLE_MEDIA[format] });
}

async function transport(method: string, path: string, url: string, payload: string | undefined,
  cfg: Omit<NomosConfig, 'tenant'> & { tenant?: string }, tenantScoped: boolean, send: () => Promise<RawResponse>,
  priority?: ReadPriority): Promise<RawResponse> {
  let raw: RawResponse;
  if (isRead(method, path)) {
    // identical reads share one request (and, per ./cache readPolicy, a short-lived answer); see ./cache.
    // Distinct reads issued together travel as one POST /batch; see ./batch.
    const batchTenant = tenantScoped ? cfg.tenant ?? '' : activeTenantOrEmpty();
    const read = {
      baseUrl: cfg.baseUrl, tenant: batchTenant, token: cfg.token, method: method.toUpperCase(),
      path: url.slice(cfg.baseUrl.length), payload, send, priority,
    };
    // an identical read another card already queued is the one this caller will share (below): it moves up instead
    if (priority === 'high') prioritise(read);
    const viaBatch = () => batchedRead(read);
    raw = await sharedRead(`${method.toUpperCase()} ${url}\n${payload ?? ''}\n${cfg.token ?? ''}`, readPolicy(method, path, tenantScoped), viaBatch);
  } else {
    try { raw = await send(); } finally { noteWrite(path, tenantScoped); }
  }
  return raw;
}

// ---- Network quiet (background work waits for the page's own requests) ----
const activity = { active: 0, last: 0 };

/**
 * Resolves once no NomOS request has been in flight for `quietMs`: the page the person opened has its data.
 * Background prefetches (the dashboard preload) wait for this so they never compete with the page's own reads.
 */
export function whenNomosQuiet(quietMs: number, opts: { now?: () => number; poll?: number } = {}): Promise<void> {
  const now = opts.now ?? Date.now;
  const poll = opts.poll ?? Math.min(250, quietMs);
  return new Promise((resolve) => {
    const check = () => {
      const idleFor = now() - activity.last;
      if (activity.active === 0 && idleFor >= quietMs) resolve();
      else setTimeout(check, activity.active === 0 ? Math.max(quietMs - idleFor, 10) : poll);
    };
    setTimeout(check, quietMs);
  });
}

/** The active tenant when one is known (an unscoped read such as /me may then ride that tenant's batch), else ''. */
function activeTenantOrEmpty(): string {
  try { return activeTenantSlug(); } catch { return ''; }
}

/** Config for calls outside a tenant (/auth/*, /me): no tenant needed. */
function unscopedConfig(): Omit<NomosConfig, 'tenant'> {
  const root = nomosRoot();
  if (root === null) throw new Error('NomOS: VITE_NOMOS_API_URL is not set');
  return { baseUrl: root, token: override?.token ?? localToken() ?? undefined };
}

/** Unwrap a typical cell value (scalar | [scalar] | {value}) to a scalar. Reshape only. */
export function scalar(v: unknown): unknown {
  if (Array.isArray(v)) return scalar(v[0]);
  if (v && typeof v === 'object' && 'value' in (v as object)) return scalar((v as { value: unknown }).value);
  return v ?? undefined;
}

/** slug -> attribute_id per object type (records/values keys cells by attribute_id, not slug). Cached per base+tenant. */
const attrCache = new Map<string, Promise<Record<string, string>>>();
export function clearAttributeCache(): void { attrCache.clear(); }
registerTenantCache(clearAttributeCache);
onAttributeInvalidation((type) => {
  if (type === null) { attrCache.clear(); return; }
  for (const k of [...attrCache.keys()]) if (k.endsWith(`|${type}`)) attrCache.delete(k);
});
export function attributeIds(type: string): Promise<Record<string, string>> {
  const cfg = getConfig();
  const key = `${cfg.baseUrl}|${cfg.tenant}|${type}`;
  let p = attrCache.get(key);
  if (!p) {
    p = nomosFetch<NomosEnvelope<Array<{ slug: string; attribute_id: string }>>>('GET', `/objects/${encodeURIComponent(type)}/attributes`)
      .then((r) => Object.fromEntries((r.data ?? []).map((a) => [a.slug, a.attribute_id])));
    p.catch(() => attrCache.delete(key));
    attrCache.set(key, p);
  }
  return p;
}

/** Normalise a reference cell ({target_record_id,target_kenning}) / array of them to ids+names. Reshape only. */
export function refs(v: unknown): Array<{ id: string; name: string }> {
  const arr = Array.isArray(v) ? v : v == null ? [] : [v];
  return arr.filter((x) => x && typeof x === 'object' && 'target_record_id' in (x as object))
    .map((x) => ({ id: (x as { target_record_id: string }).target_record_id, name: (x as { target_kenning?: string }).target_kenning ?? '' }));
}

/** records/query with `projection` (the page's cells inline, keyed by slug: ONE read). A NomOS without projection
 *  answers rows without `values`: then records/values for the requested `fields`. `cursor` is an offset string. */
export async function queryRecords(
  type: string,
  opts: { filter?: NomosFilter; fields?: string[]; limit?: number; cursor?: string | number; sorts?: Array<{ field: string; direction?: 'asc' | 'desc' }>;
    /** bounds calculated attributes (ISO datetimes); NomOS defaults to a trailing 365-day window */ period?: { start: string; end: string } } = {},
): Promise<NomosPage> {
  const limit = opts.limit ?? 50;
  const offset = opts.cursor === undefined ? 0 : Number(opts.cursor);
  const q: Record<string, unknown> = { limit, offset };
  if (opts.filter) q.filter = opts.filter;
  if (opts.sorts?.length) q.sorts = opts.sorts;
  const project = !!opts.fields?.length;
  if (project) {
    q.projection = opts.fields;
    if (opts.period) { q.period_start = opts.period.start; q.period_end = opts.period.end; }
  }
  const res = await nomosFetch<NomosEnvelope<Array<{ record_id: string; kenning: string; values?: Record<string, unknown> }>>>(
    'POST', `/objects/${encodeURIComponent(type)}/records/query`, q);
  const refsRows = res.data ?? [];
  const total = res.meta?.pagination?.total ?? refsRows.length;
  if (!project || refsRows.every((r) => r.values !== undefined)) {
    return { rows: refsRows.map((r) => ({ record_id: r.record_id, kenning: r.kenning, values: r.values ?? {} })), total, limit, offset };
  }
  const byId: Record<string, Record<string, unknown>> = {};
  if (refsRows.length) {
    const [vr, ids] = await Promise.all([
      nomosFetch<NomosEnvelope<{ values?: Record<string, Record<string, unknown>> }>>(
        'POST', `/objects/${encodeURIComponent(type)}/records/values`,
        { record_ids: refsRows.map((r) => r.record_id), attributes: opts.fields,
          ...(opts.period ? { period_start: opts.period.start, period_end: opts.period.end } : {}) }),
      attributeIds(type),
    ]);
    const slugOf = Object.fromEntries(Object.entries(ids).map(([slug, id]) => [id, slug]));
    for (const [rid, cells] of Object.entries(vr.data?.values ?? {})) {
      byId[rid] = Object.fromEntries(Object.entries(cells).map(([aid, v]) => [slugOf[aid] ?? aid, v]));
    }
  }
  return { rows: refsRows.map((r) => ({ record_id: r.record_id, kenning: r.kenning, values: byId[r.record_id] ?? {} })), total, limit, offset };
}

export async function getRecord(type: string, id: string): Promise<NomosEnvelope<{ record_id: string; kenning: string; values: Record<string, unknown> }>> {
  return nomosFetch('GET', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(id)}`);
}

export async function patchRecord(type: string, id: string, values: Record<string, unknown>): Promise<NomosEnvelope<unknown>> {
  return nomosFetch('PATCH', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(id)}`, { values });
}

export async function invokeVerb(verb: string, parameters: Record<string, unknown> = {}, mode: 'dry_run' | 'apply' | 'propose' = 'apply'): Promise<NomosEnvelope<unknown>> {
  return nomosFetch('POST', `/actions/${encodeURIComponent(verb)}/invoke`, { parameters, mode });
}

/**
 * The engine's typed NO-DATA refusals (422): no accessible record matches the scope (`empty_scope`), or records match
 * but no reading contributes in the window (`no_telemetry`). Both answer "there is no value" - a building with no
 * meters, a comparison window before the data history - so a screen shows its empty value, never a failed card
 * (sweep 2026-10-13: /buildings and /budget site stats failed on them). Budget refusals (too large, too slow) stay errors.
 */
export const NO_DATA_REFUSALS: ReadonlySet<string> = new Set(['empty_scope', 'no_telemetry']);

/** The refusal code of a no-data answer, else null. */
export function noDataRefusal(e: unknown): string | null {
  const r = (e as AxiosError<{ error?: { code?: string } }>)?.response;
  const code = r?.status === 422 ? r.data?.error?.code : undefined;
  return code && NO_DATA_REFUSALS.has(code) ? code : null;
}

/**
 * The query engine (reduce: metrics x dimensions x time buckets). Body is passed through verbatim.
 * A no-data refusal comes back as an empty frame (`rows: []`, `meta.no_data` = the refusal code and message).
 */
export async function runQuery<T = unknown>(body: Record<string, unknown>, opts: { priority?: ReadPriority } = {}): Promise<NomosEnvelope<T>> {
  try {
    return await nomosFetch('POST', '/queries', body, opts.priority ? { priority: opts.priority } : {});
  } catch (e) {
    const code = noDataRefusal(e);
    if (!code) throw e;
    return { data: { rows: [], meta: { no_data: { code, message: (e as Error).message } } } as unknown as T };
  }
}

export async function catalogSearch<T = unknown>(body: Record<string, unknown>): Promise<NomosEnvelope<T>> {
  return nomosFetch('POST', '/catalog/search', body);
}

export interface NomosMe { member_id: string; tenant_id: string; email: string; name?: string | null; is_admin?: boolean; sentinel_user_id?: string | null; [k: string]: unknown }

/** GET /me (not tenant-scoped). The principal of the bearer token: one member of ONE tenant (tenant_id). */
export async function getMe(): Promise<NomosMe> {
  return nomosFetch('GET', '/me', undefined, { tenantScoped: false });
}

export interface NomosRecordGroup { key: unknown[]; kennings: Array<string | null>; count: number }

/** records/query with `group_by` (attribute paths) -> RecordGroup[] counted by NomOS. Pages through every group. */
export async function groupRecords(type: string, opts: { groupBy: string[]; filter?: NomosFilter; limit?: number }): Promise<NomosRecordGroup[]> {
  const limit = opts.limit ?? 1000;
  const out: NomosRecordGroup[] = [];
  for (let offset = 0; ; offset += limit) {
    const body: Record<string, unknown> = { group_by: opts.groupBy, limit, offset };
    if (opts.filter) body.filter = opts.filter;
    const res = await nomosFetch<NomosEnvelope<NomosRecordGroup[]>>('POST', `/objects/${encodeURIComponent(type)}/records/query`, body);
    const rows = res.data ?? [];
    out.push(...rows);
    const total = res.meta?.pagination?.total ?? out.length;
    if (rows.length === 0 || out.length >= total) break;
  }
  return out;
}

/** NomOS timestamps end in `+00:00`; Sentinel's end in `Z`. Same instant, Sentinel's spelling (reshape only). */
export const zulu = <T extends string | null | undefined>(s: T): T => (typeof s === 'string' ? (s.replace(/\+00:00$/, 'Z') as T) : s);
